import { EXAM_EVENT_DATA_TEST_ID } from '@e2e/constants/exam-center';
import { SeatFormField, sortRooms } from '@exam-center/exam-event-management';
import {
  CircularProgress,
  Flex,
  FormCheckbox,
  FormHelperText,
  styled,
  Text,
} from '@shared/components';
import { memo } from 'react';

import { type RoomData } from '../../containers/exam-event-creation-logic/exam-event-creation-logic';

import { EmptyState } from './empty-state';
import { RoomInfo } from './room-info';

const RoomListWrapper = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(3),
  border: '1px solid',
  padding: theme.spacing(4),
  borderColor: theme.palette.grey[300],
  borderRadius: theme.shape.rounded(2),
  backgroundColor: theme.palette.grey[50],
}));

interface RoomSelectionFormFieldProps {
  rooms: RoomData[];
  isLoading: boolean;
}

function RoomSelectionFormField(props: Readonly<RoomSelectionFormFieldProps>) {
  const { rooms, isLoading } = props;

  if (isLoading) {
    return (
      <RoomListWrapper
        sx={(theme) => ({
          gap: theme.spacing(2),
          justifyContent: 'center',
          alignItems: 'center',
        })}
      >
        <CircularProgress />
        <Text variant='body2'>กำลังโหลดข้อมูลห้องสอบ...</Text>
      </RoomListWrapper>
    );
  }

  if (rooms.length === 0) {
    return (
      <RoomListWrapper
        sx={(theme) => ({
          gap: theme.spacing(2),
          justifyContent: 'center',
          alignItems: 'center',
        })}
      >
        <EmptyState />
      </RoomListWrapper>
    );
  }

  return (
    <RoomListWrapper
      data-testid={EXAM_EVENT_DATA_TEST_ID.ROOM_SELECTION_FORM_FIELD}
      sx={(theme) => ({
        gap: theme.spacing(3),
      })}
    >
      {[...rooms].sort(sortRooms).map((room: RoomData, index) => {
        return (
          <FormCheckbox
            key={room.id}
            name={`location.room.${room.id}`}
            data-testid={`room_${index + 1}`}
            label={
              <Flex
                id={index.toString()}
                sx={(theme) => ({
                  columnGap: theme.spacing(3),
                  flexWrap: 'wrap',
                })}
              >
                <RoomInfo label='อาคาร' value={room.building} />
                <RoomInfo label='ชั้น' value={room.floor} />
                <RoomInfo label='ห้อง' value={room.name} />
                <RoomInfo label='เลขห้อง' value={room.roomNumber ?? '-'} />
                <RoomInfo label='จำนวนที่นั่ง' value={`${room.seat} ที่นั่ง`} />
              </Flex>
            }
            sx={(theme) => ({
              p: theme.spacing(0),
              color: theme.palette.grey[400],
            })}
            slotProps={{
              formControlLabel: {
                sx(theme) {
                  return {
                    gap: theme.spacing(2),
                    alignItems: 'center',
                  };
                },
              },
            }}
          />
        );
      })}
    </RoomListWrapper>
  );
}

interface RoomSelectionWithSeatFormFieldsProps extends RoomSelectionFormFieldProps {
  disableSeatFormField?: boolean;
  numberOfExaminees?: number;
  isShowSeatInfo?: boolean;
  roomSelectionError?: string;
}

function RoomSelectionWithSeatFormFields(props: Readonly<RoomSelectionWithSeatFormFieldsProps>) {
  const {
    disableSeatFormField,
    numberOfExaminees,
    isShowSeatInfo = false,
    roomSelectionError,
    ...restProps
  } = props;

  return (
    <>
      <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
        <Text variant='body1'>เลือกห้องสอบ (สามารถเลือกได้หลายห้อง)</Text>
        <RoomSelectionFormField {...restProps} />
        {roomSelectionError ? <FormHelperText error>{roomSelectionError}</FormHelperText> : null}
      </Flex>
      <SeatFormField
        disabled={disableSeatFormField}
        endContent={
          isShowSeatInfo && (
            <Text variant='body2' sx={{ py: (theme) => theme.spacing(2.5) }}>
              (จำนวนที่ลงทะเบียน: {numberOfExaminees} ที่นั่ง)
            </Text>
          )
        }
      />
    </>
  );
}

export default memo(RoomSelectionWithSeatFormFields);
